// src/pages/SubscriberLoginPage.jsx
// Route: /subscriber-login  (and /subscriber-register via the toggle below)

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSubscriber } from '../context/SubscriberContext';
import { subscriberAPI } from '../services/api';
import { useToast } from '../context/ToastContext';
import previuLogo from '../assets/previu-logo.jpeg';

const SUPPORTED_COUNTRIES = [
  { code: 'AE', label: 'United Arab Emirates' },
  { code: 'IN', label: 'India' },
];

export default function SubscriberLoginPage() {
  const [mode, setMode] = useState('login');
  const navigate = useNavigate();
  const { storeSession } = useSubscriber();
  const { toast } = useToast();

  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName]   = useState('');
  const [phone, setPhone]         = useState('');
  const [country, setCountry]     = useState('AE');
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');

  async function handleLogin(e) {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const res = await subscriberAPI.login({ email, password });
      storeSession(res.data);
      toast.success('Welcome back!');
      navigate('/subscriber/dashboard');
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid email or password.');
    } finally {
      setLoading(false);
    }
  }

  async function handleRegister(e) {
    e.preventDefault();
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    setError(''); setLoading(true);
    try {
      const res = await subscriberAPI.register({
        firstName, lastName, email, phone, password,
        country, programCategory: 'GUARDIAN_DUBAI',
      });
      storeSession(res.data);
      toast.success('Account created!');
      navigate('/subscriber/dashboard');
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Could not create account.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="login-page">
      <div className="login-card fade-in-up">
        <div className="login-logo-area">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: -6 }}>
            <img src={previuLogo} alt="Previu" style={{ height: 140, width: 'auto' }} />
          </div>
          <div className="login-tagline">Guardian Dubai</div>
        </div>

        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          background: 'var(--amber-bg)', borderRadius: 99,
          padding: '5px 14px', fontSize: 12, color: 'var(--brand-accent)',
          fontWeight: 700, marginBottom: 20,
        }}>
          🛡️ Subscriber Portal
        </div>

        <div className="login-title">{mode === 'login' ? 'Welcome back' : 'Create your account'}</div>
        <div className="login-sub">
          {mode === 'login' ? 'Sign in to view your family’s health.' : 'Join Guardian Dubai to enroll your family.'}
        </div>

        {error && (
          <div className="alert alert-error">
            ⚠️ {error}
          </div>
        )}

        <form onSubmit={mode === 'login' ? handleLogin : handleRegister}>
          {mode === 'register' && (
            <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
              <div className="form-group" style={{ flex: 1 }}>
                <label className="form-label" htmlFor="sub-first-name">First name</label>
                <input id="sub-first-name" className="login-input" value={firstName}
                  onChange={e => setFirstName(e.target.value)} required />
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label className="form-label" htmlFor="sub-last-name">Last name</label>
                <input id="sub-last-name" className="login-input" value={lastName}
                  onChange={e => setLastName(e.target.value)} required />
              </div>
            </div>
          )}

          <div className="form-group" style={{ marginBottom: 16 }}>
            <label className="form-label" htmlFor="sub-email">Email</label>
            <input id="sub-email" type="email" className="login-input" value={email}
              onChange={e => setEmail(e.target.value)} required autoComplete="email" />
          </div>

          {mode === 'register' && (
            <div className="form-group" style={{ marginBottom: 16 }}>
              <label className="form-label" htmlFor="sub-phone">Phone (optional)</label>
              <input id="sub-phone" className="login-input" value={phone}
                onChange={e => setPhone(e.target.value)} />
            </div>
          )}

          {mode === 'register' && (
            <div className="form-group" style={{ marginBottom: 16 }}>
              <label className="form-label" htmlFor="sub-country">Country</label>
              <select id="sub-country" className="login-input" value={country}
                onChange={e => setCountry(e.target.value)}>
                {SUPPORTED_COUNTRIES.map(c => (
                  <option key={c.code} value={c.code}>{c.label}</option>
                ))}
              </select>
            </div>
          )}

          <div className="form-group" style={{ marginBottom: 8 }}>
            <label className="form-label" htmlFor="sub-password">Password</label>
            <input id="sub-password" type="password" className="login-input" value={password}
              onChange={e => setPassword(e.target.value)} required
              autoComplete={mode === 'login' ? 'current-password' : 'new-password'} />
          </div>

          <button className="login-btn" type="submit" disabled={loading}>
            {loading ? 'Please wait…' : mode === 'login' ? 'Sign in' : 'Create account'}
          </button>
        </form>

        <div style={{ fontSize: 13, color: 'var(--text-secondary)', textAlign: 'center', marginTop: 20 }}>
          {mode === 'login' ? (
            <>New to Guardian?{' '}
              <button type="button" onClick={() => { setMode('register'); setError(''); }}
                style={{ background: 'none', border: 'none', color: 'var(--brand-accent)', fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font-body)' }}>
                Create an account
              </button>
            </>
          ) : (
            <>Already have an account?{' '}
              <button type="button" onClick={() => { setMode('login'); setError(''); }}
                style={{ background: 'none', border: 'none', color: 'var(--brand-accent)', fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font-body)' }}>
                Sign in
              </button>
            </>
          )}
        </div>

        <div style={{ textAlign: 'center', marginTop: 12, fontSize: 12, color: 'var(--text-muted)' }}>
          Secured by Previu Health
        </div>
      </div>
    </div>
  );
}
